import { useState, useEffect } from 'react';
import { Button, Input } from '@repo/o37-ui';
import { supabase } from '../../supabaseClient';

interface PasswordGateProps {
  deckSlug: string;
  deckName: string;
  logoUrl?: string;
  children: React.ReactNode;
}

export default function PasswordGate({ deckSlug, deckName, logoUrl, children }: PasswordGateProps) {
  const [password, setPassword] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Check session on mount
  useEffect(() => {
    const sessionKey = `deck_session_${deckSlug}`;
    const session = sessionStorage.getItem(sessionKey);

    if (session) {
      try {
        // Validate session hasn't expired (24 hours)
        const sessionData = JSON.parse(session);
        const now = Date.now();
        if (now - sessionData.timestamp < 24 * 60 * 60 * 1000) {
          setIsUnlocked(true);
        } else {
          sessionStorage.removeItem(sessionKey);
        }
      } catch {
        // Corrupted session data, remove it
        sessionStorage.removeItem(sessionKey);
      }
    }

    setIsLoading(false);
  }, [deckSlug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsVerifying(true);

    try {
      // Call the RPC function to verify password
      const { data, error: rpcError } = await supabase
        .rpc('verify_deck_password', {
          p_deck_slug: deckSlug,
          p_password: password
        });

      if (rpcError) {
        throw rpcError;
      }

      if (data === true) {
        // Password is correct - save session
        const sessionKey = `deck_session_${deckSlug}`;
        sessionStorage.setItem(sessionKey, JSON.stringify({
          timestamp: Date.now(),
          verified: true
        }));
        setIsUnlocked(true);
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      console.error('Password verification error:', err);
      setError(err instanceof Error ? err.message : 'Failed to verify password');
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-neutral-600">Loading...</div>
      </div>
    );
  }

  if (isUnlocked) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-neutral-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-neutral-200 p-8">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={deckName}
              className="h-12 w-auto object-contain"
            />
          ) : (
            <div className="h-12 w-12 bg-neutral-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center mb-2 text-neutral-900">
          {deckName}
        </h1>
        <p className="text-center text-neutral-500 mb-8 text-sm">
          This deck is password protected
        </p>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password..."
              required
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={isVerifying || !password}
            text={isVerifying ? 'Verifying...' : 'Unlock Deck'}
            className="w-full"
          />
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-neutral-100 text-center">
          <a
            href="mailto:IT@o37.co"
            className="text-xs text-neutral-500 underline underline-offset-2 decoration-neutral-300 hover:text-neutral-700 hover:decoration-neutral-500"
          >
            Don't have a password?
          </a>
        </div>
      </div>
    </div>
  );
}
